import { Checkbox, Select, Switch, Text } from "@ttc3k/trekker";
import { ControlledInput } from "shared/components/formControl/ControlledInput";
import { ControlledSelectRoot } from "shared/components/formControl/ControlledSelectRoot";
import { Flex } from "styled-system/jsx";
import { useEntityOptions, useTagGroupsCollection } from "../../hooks";
import {
  EnumTagEntityTypes,
  EnumTagGroupKey,
  EnumTagGroupType,
  EnumTagSuggestionEntityTypes,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";
import {
  getTagEntityTypesForTagGroup,
  TagInfoFormValues,
} from "./tagInfoFormSchema";
import { css } from "styled-system/css";
import { useFeatureFlag } from "shared/contextProviders/featureFlags";
import { FeatureFlags } from "shared/featureFlags";

type TagInformationFormProps = {
  flyoutCategoryOptions: {
    value: string;
    label: string;
    key: EnumTagGroupKey;
    type: EnumTagGroupType;
  }[];
  isAdminland: boolean;
  /** When true, show and enable the "Apply to" entity type checkboxes (e.g. in org Custom Tags edit) */
  enableEntityTypeSelection?: boolean;
};

export const TagInformationForm = ({
  flyoutCategoryOptions,
  isAdminland,
  enableEntityTypeSelection = false,
}: TagInformationFormProps) => {
  const { t } = useTranslation("adminland", {
    keyPrefix: "PAGE.TAG_MANAGER.FLYOUTS.COMMON_FORM",
  });

  const { entityOptions } = useEntityOptions();
  const { tagGroupsCollection } = useTagGroupsCollection({
    flyoutCategoryOptions,
  });

  const { control, setValue } = useFormContext<TagInfoFormValues>();

  const tagGroup = useWatch({ control, name: "tagGroup" });
  const entityTypes = useWatch({ control, name: "entityTypes" });
  const inheritable = useWatch({ control, name: "inheritable" });

  const membershipTiersEnabled = useFeatureFlag(FeatureFlags.MembershipTiers);
  const isListingTag =
    entityTypes.includes(EnumTagEntityTypes.Operator as any) ||
    entityTypes.includes(EnumTagSuggestionEntityTypes.Operator as any);

  /**
   * When the tag group changes, update the tagGroup and entity types
   * @param param0
   * @returns
   */
  const handleTagGroupChange = ({ value }: { value: string[] }) => {
    const tagGroup = value[0];
    setValue("tagGroup", tagGroup, { shouldDirty: true });

    const tagGroupDetails = flyoutCategoryOptions.find(
      option => option.value === tagGroup,
    );
    if (!tagGroupDetails) return;

    setValue(
      "entityTypes",
      getTagEntityTypesForTagGroup(tagGroupDetails?.type, tagGroupDetails?.key),
      {
        shouldDirty: true,
      },
    );
  };

  return (
    <Flex flexDir="column" gap="250">
      {isAdminland && (
        <ControlledSelectRoot
          name="tagGroup"
          label={t("CATEGORY")}
          collection={tagGroupsCollection}
          value={[tagGroup]}
          onValueChange={handleTagGroupChange}
          className={css({
            "& [data-part='value-text']": {
              lineClamp: 1,
              wordBreak: "break-all",
            },
          })}
        >
          {tagGroupsCollection.items.map(item => (
            <Select.Item
              key={item.value}
              item={item}
              className={css({
                "& [data-part='item-text']": {
                  lineClamp: 1,
                  wordBreak: "break-all",
                },
              })}
            />
          ))}
        </ControlledSelectRoot>
      )}

      <ControlledInput name="name" label={t("NAME")} />

      {(isAdminland || enableEntityTypeSelection) && (
        <Flex flexDir={"column"} gap={"150"}>
          <Text>{t("APPLY_TO")}</Text>
          <Flex flexDir={"column"} gap={"100"}>
            {(enableEntityTypeSelection
              ? entityOptions.filter(
                  opt => opt.value !== EnumTagSuggestionEntityTypes.Person,
                )
              : entityOptions
            ).map(option => (
              <Checkbox
                key={option.value}
                label={option.label}
                checked={entityTypes.includes(option.value)}
                onCheckedChange={e =>
                  setValue(
                    "entityTypes",
                    e.checked === true
                      ? [...entityTypes, option.value]
                      : entityTypes.filter(type => type !== option.value),
                    { shouldDirty: true },
                  )
                }
                disabled={!(isAdminland || enableEntityTypeSelection)}
                alignItems="center"
                gap="200"
              />
            ))}
          </Flex>
        </Flex>
      )}

      {enableEntityTypeSelection && isListingTag && membershipTiersEnabled && (
        <Flex flexDir={"column"} gap={"150"}>
          <Flex alignItems="center" gap="150">
            <Switch
              checked={inheritable ?? false}
              onCheckedChange={details =>
                setValue("inheritable", details.checked, { shouldDirty: true })
              }
            />
            <Text>{t("INHERITABLE")}</Text>
          </Flex>
          <Text color="text.mid" textStyle="body.sm">
            {t("INHERITABLE_DESCRIPTION")}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};
